import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FetchData, { GetListAsync, GetOne } from "../../providers/dataProvider";
import { formatDash, formatDate } from "../../utils/Validate";
import "../../assets/styles/approvalQuoteModal.css";


function ApprovalBlockModal() {
    const navigate = useNavigate();
    const location = useLocation();
    const [pendingList, setPendingList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const fetchQuotationDetails = useCallback(async (quotationId) => {
        try {
            const result = await new Promise((resolve) => {
                GetOne("quotation", { id: quotationId }, (res) => resolve(res));
            });
            if (result?.message !== "success") return {};
            return result.data || {};
        } catch { return {}; }
    }, []);

    const fetchPendingList = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            const result = await GetListAsync("quotationPending", {});
            if (result?.message === "success") {
                const rawList = Array.isArray(result?.data) ? result.data : [];
                const enrichedList = await Promise.all(
                    rawList.map(async (item) => {
                        const details = await fetchQuotationDetails(item.quotationId);
                        return {
                            ...item,
                            products: details.products ?? [],
                        };
                    })
                );
                setPendingList(enrichedList);
            } else {
                setErrorMessage("Failed to load pending approvals. Please refresh.");
            }
        } catch {
            setErrorMessage("Network error. Please refresh the page.");
        } finally {
            setIsLoading(false);
        }
    }, [fetchQuotationDetails]);

    const FormatProductValue = (listPrice, discount) => {
        const price = parseFloat(listPrice) || 0;
        const disc = parseFloat(discount) || 0;
        const result = price - (price * disc / 100);
        if (isNaN(result)) return "0.00";
        return result.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    useEffect(() => { fetchPendingList(); }, []);

    useEffect(() => {
        const handleApprovalDone = () => { fetchPendingList(); };
        window.addEventListener("approvalStatusChanged", handleApprovalDone);
        return () => window.removeEventListener("approvalStatusChanged", handleApprovalDone);
    }, [fetchPendingList]);

    const handleView = useCallback((item) => {
        navigate('/job-in-hand', {
            state: {
                id: item.quotationId,
                initialTab: "Details",
                initialProductTab: "ProductList"
            },
        });
    }, [navigate]);

    if (isLoading) return null;
    if (pendingList.length === 0) return null;
    if (location.pathname === '/job-in-hand') return null;

    return (
        <div className="abm-overlay">
            <div className="abm-modal">
                <div className="abm-header">
                    <span className="abm-header-icon">⚠</span>
                    <div>
                        <h2 className="abm-title">Quotation Approval Required</h2>
                        <p className="abm-subtitle">
                            You have{" "}
                            <strong>{pendingList.length}</strong>{" "}
                            pending approval{pendingList.length !== 1 ? "s" : ""}.
                            Please review all items.
                        </p>
                    </div>
                </div>

                {errorMessage && (
                    <div className="abm-error-banner">
                        <span>⚠  {errorMessage}</span>
                        <button className="abm-error-close" onClick={() => setErrorMessage("")}>
                            ✕
                        </button>
                    </div>
                )}

                <div className="abm-section-label">
                    <span className="abm-red-dot" />
                    PENDING REVIEW — {pendingList.length} item{pendingList.length !== 1 ? "s" : ""}
                </div>

                <div style={{ overflowX: "auto" }}>
                    <table className="abm-table">
                        <thead>
                            <tr>
                                {[
                                    "Quote Ref", "Customer", "Sales Engineer", "Project",
                                    "Products", "List price", "Discount", "Net Price", "Requested At",
                                    "Status", "Action",
                                ].map((heading) => (
                                    <th key={heading}>{heading}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {pendingList.map((item) => {
                                const products = Array.isArray(item.products) && item.products.length > 0
                                    ? item.products.filter(p => p.isActionableForCurrentUser === true)
                                    : [null];

                                return products.map((product, index) => (
                                    <tr key={`${item.approvalRequestId}-${index}`} className="abm-row">
                                        {index === 0 && (
                                            <>
                                                <td className="abm-quote-ref" rowSpan={products.length}>
                                                    {formatDash(item.quoteReference)}
                                                </td>
                                                <td rowSpan={products.length}>{formatDash(item.customerName)}</td>
                                                <td rowSpan={products.length}>{formatDash(item.engineerName)}</td>
                                                <td rowSpan={products.length}>{formatDash(item.projectName)}</td>
                                            </>
                                        )}
                                        <td>
                                            <span className="abm-product-text">
                                                {product
                                                    ? (product.productName ?? product.name ?? "Unknown")
                                                    : "—"}
                                            </span>
                                        </td>
                                        <td>
                                            {product ? (
                                                <span className="abm-value-badge">
                                                    {FormatProductValue(product.listPrice, product.margin)}
                                                </span>
                                            ) : "—"}
                                        </td>
                                        <td>
                                            {product?.margin != null ? (
                                                <span className="abm-discount-badge">
                                                    {product.margin > 0 ? "+" : ""}{product.margin}%
                                                </span>
                                            ) : "—"}
                                        </td>
                                        <td>
                                            {product ? (
                                                <span className="abm-net-price-badge">
                                                    {product.sellingPrice != null
                                                        ? Number(product.sellingPrice).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                        : FormatProductValue(product.listPrice, product.margin)}
                                                </span>
                                            ) : "—"}
                                        </td>
                                        {index === 0 && (
                                            <>
                                                <td rowSpan={products.length}>{formatDate(item.requestedAt)}</td>
                                                <td rowSpan={products.length}>
                                                    <span className="abm-status-badge">
                                                        {item.status ?? "Pending"}
                                                    </span>
                                                </td>
                                                <td rowSpan={products.length}>
                                                    <button
                                                        className="abm-view-btn abm-btn"
                                                        onClick={() => handleView(item)}
                                                    >
                                                        View
                                                    </button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ));
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="abm-footer">
                    🔒 You cannot access other sections until all approvals are resolved.
                </div>
            </div>
        </div>
    );
}

export default ApprovalBlockModal;